// Generated by GitHub Copilot
import React, { useMemo, useState } from "react";
import { Row, Col, Alert, Button } from "react-bootstrap";
import Select from "react-select";
import { FaFilter } from "react-icons/fa";
import ImpactRecordCard from "./ImpactRecordCard";
import styles from "./ImpactRecordsList.module.css";

// Options for react-select dropdowns
const STATUS_FILTER_OPTIONS = [
  { label: "All Statuses", value: "all" },
  { label: "Active", value: "active" },
  { label: "Resolved", value: "resolved" },
  // Legacy status filters for backward compatibility
  { label: "Critical", value: "critical" },
  { label: "Degraded", value: "degraded" },
  { label: "Recovered", value: "recovered" },
];

const PRIORITY_FILTER_OPTIONS = [
  { label: "All Priorities", value: "all" },
  { label: "P1 - Critical", value: "P1" },
  { label: "P2 - High", value: "P2" },
  { label: "P3 - Medium", value: "P3" },
  { label: "P4 - Low", value: "P4" },
];

const ImpactRecordsList = ({
  impacts,
  onEdit,
  onDelete,
  user,
}) => {
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");

  const currentUserEmail = user?.email || "";

  // Get selected option objects for react-select
  const getStatusOption = () => {
    return STATUS_FILTER_OPTIONS.find((opt) => opt.value === filterStatus);
  };

  const getPriorityOption = () => {
    return PRIORITY_FILTER_OPTIONS.find((opt) => opt.value === filterPriority);
  };

  // Filter and sort impacts
  const filteredAndSortedImpacts = useMemo(() => {
    let filtered = [...impacts];

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((impact) => impact.status === filterStatus);
    }

    // Apply priority filter
    if (filterPriority !== "all") {
      filtered = filtered.filter(
        (impact) => impact.priority === filterPriority
      );
    }

    // Sort impacts: user's impacts first, then active status, then by date
    const statusOrder = { active: 0, resolved: 1 };
    return filtered.sort((a, b) => {
      // Primary sort: user's impacts first
      const aIsUserImpact = a.createdBy === currentUserEmail;
      const bIsUserImpact = b.createdBy === currentUserEmail;
      if (aIsUserImpact !== bIsUserImpact) {
        return aIsUserImpact ? -1 : 1;
      }

      // Secondary sort: active first, then resolved
      const orderA = statusOrder[a.status] ?? 999;
      const orderB = statusOrder[b.status] ?? 999;
      if (orderA !== orderB) return orderA - orderB;

      // Tertiary sort by created date (newest first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [impacts, filterStatus, filterPriority, currentUserEmail]);

  const hasActiveFilters = filterStatus !== "all" || filterPriority !== "all";

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterPriority("all");
  };

  if (impacts.length === 0) {
    return (
      <Alert variant="info" className={styles.emptyState}>
        <Alert.Heading>No Impact Records</Alert.Heading>
        <p>No impacts have been documented for this incident yet.</p>
      </Alert>
    );
  }

  return (
    <div className={styles.impactsList}>
      {/* Filters Section */}
      <div className={styles.filtersSection}>
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6}>
            <Select
              classNamePrefix="rs"
              value={getStatusOption()}
              onChange={(option) => setFilterStatus(option?.value || "all")}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Filter by Status"
              isClearable={false}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Select
              classNamePrefix="rs"
              value={getPriorityOption()}
              onChange={(option) => setFilterPriority(option?.value || "all")}
              options={PRIORITY_FILTER_OPTIONS}
              placeholder="Filter by Priority"
              isClearable={false}
            />
          </Col>
        </Row>

        {hasActiveFilters && (
          <Row>
            <Col xs={5} sm={5} md={6} lg={9}>
              {filteredAndSortedImpacts.length} of {impacts.length} impacts
            </Col>
            <Col xs={5} sm={4} md={3} lg={3}>
              <Button variant="link" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </Col>
          </Row>
        )}
      </div>

      {/* Results */}
      {filteredAndSortedImpacts.length === 0 ? (
        <Alert variant="warning">
          <Alert.Heading>
            <FaFilter className="me-2" />
            No Matching Results
          </Alert.Heading>
          <p>
            No impacts match your current filters. Try adjusting your search
            criteria.
          </p>
          <Button variant="outline-warning" size="sm" onClick={clearFilters}>
            Clear all filters
          </Button>
        </Alert>
      ) : (
        <Row>
          {filteredAndSortedImpacts.map((impact) => (
            <Col key={impact.id} xs={12} lg={6} className="mb-3">
              <ImpactRecordCard
                impact={impact}
                onEdit={onEdit}
                onDelete={onDelete}
                currentUserEmail={currentUserEmail}
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default ImpactRecordsList;
